import React, { useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography
} from "@mui/material";

import type { IRemoveEntityDialogProps } from "./EntitiesTab.types";

/**
 * Confirmation dialog for removing an entity from a client.
 *
 * Displays the entity name and the exact copy required by the spec.
 * If linkedDealsCount > 0, includes an additional warning about deal file
 * visibility being affected.
 * The "Remove" button is styled red (per existing delete-dialog conventions).
 */
export const RemoveEntityDialog: React.FC<IRemoveEntityDialogProps> = ({
  open,
  entityName,
  linkedDealsCount,
  onConfirm,
  onCancel
}) => {
  const hasLinkedDeals = useMemo(() => linkedDealsCount > 0, [linkedDealsCount]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "10px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "50px",
          bgcolor: "#edf1f9",
          color: "#0a3142",
          borderRadius: "10px 10px 0 0",
          px: "34px"
        }}
      >
        <Typography>Remove Entity</Typography>
        <IconButton onClick={onCancel} sx={{ color: "#5e7a95" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: "30px", px: "45px !important" }}>
        <DialogContentText sx={{ mb: 1, fontSize: "15px" }}>
          Remove {entityName} from this client? This will affect workspace file
          visibility and portfolio calculations.
        </DialogContentText>
        {hasLinkedDeals && (
          <DialogContentText
            sx={{
              mb: 1,
              fontSize: "15px",
              fontWeight: 600,
              color: "#d32f2f"
            }}
          >
            Removing this entity will also remove its deal files from this
            client's workspace.
          </DialogContentText>
        )}
      </DialogContent>

      <DialogActions sx={{ p: "16px 24px", gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            bgcolor: "#d32f2f",
            "&:hover": { bgcolor: "#c62828" },
            textTransform: "none"
          }}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
};
